import {
  addCover,
  addModule,
  fitCover,
  setElevation,
  applyMaterial,
  computeEstimate,
  type Currency,
  duplicateModule,
  elev,
  geo,
  frontsFromPanels,
  generateDesign,
  iso,
  type ModuleDefinition,
  type ModuleInstance,
  plan,
  type ProjectData,
  moveModule,
  placeAt,
  rotateModule,
  setRotation,
  type Place,
  placeOf,
  removeModule,
  replaceModule,
  setDim,
  setFrontCount,
  templateOf,
  updateModule,
  validateProject,
} from '@core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, type Catalog, type CatalogMaterial, type ProjectDetail } from '../api';
import { canCreate, canEdit, FullScreenLoader, useAuth } from '../auth';
import { ShareDialog } from '../ShareDialog';
import { VersionsDialog } from '../editor/VersionsDialog';
import { BottomBar } from '../editor/BottomBar';
import { installEngine, sceneCfg, setShadowsPref, shadowsOn, snapshot, type Viewer } from '../editor/engine';
import { uploadFile } from '../library/upload';
import { clipboardModules, copyModules, pasteModules, removeModules } from '../editor/clipboard';
import { LeftPanel, type LeftTab } from '../editor/LeftPanel';
import { PrintSheet } from '../editor/PrintSheet';
import { RightPanel, RotateControl } from '../editor/RightPanel';
import { Viewer3D } from '../editor/Viewer3D';
import { ApprovalView } from '../approval/ApprovalView';
import { LibraryDialog } from '../library/LibraryDialog';
import { draftOfInstance, draftToInstancePatch, ModuleDesigner } from '../library/ModuleDesigner';
import { SpecWizard } from '../spec/SpecWizard';
import { SyncBadge, useSyncStatus } from '../offline/SyncBadge';
import { clearDraft, flushProject, getCatalog, getProject, type LocalSave, onSyncEvent, type SyncEvent, saveProject, stashDraft } from '../offline/sync';
import { UserMenu } from '../UserMenu';
import { Brand, Dialog, fmtMoney, Icon, MUTED, relativeTime, Svg, useToast } from '../ui';

type View = '3d' | 'planta' | 'alzado';
type Wall = 'A' | 'B' | 'C' | 'D';
const GEN_STEPS = ['Analizando medidas y aberturas', 'Ubicando fregadero junto a la toma de agua', 'Colocando electrodomésticos', 'Optimizando triángulo de trabajo y rellenos'];
type SaveState = { kind: 'saved'; at: string } | { kind: 'dirty' } | { kind: 'saving' } | { kind: 'error'; message: string };

export function EditorPage() {
  const { id = '' } = useParams();
  const { me } = useAuth();
  const nav = useNavigate();
  const { flash, toast } = useToast();

  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [data, setData] = useState<ProjectData | null>(null);
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState<Currency>('USD');
  const hist = useRef<ProjectData[]>([]);
  const fut = useRef<ProjectData[]>([]);
  const [, force] = useState(0);

  const [sel, setSelOne] = useState<number | null>(null);
  /** Other selected modules (Ctrl/⌘ + clic); `sel` stays the primary one, shown in Propiedades. */
  const [extra, setExtra] = useState<number[]>([]);
  const setSel = useCallback((id: number | null) => {
    setSelOne(id);
    setExtra([]);
  }, []);
  const [printing, setPrinting] = useState(false);
  const [view, setView] = useState<View>('3d');
  const [wall, setWall] = useState<Wall>('A');
  // 1 = Especificaciones, 2 = Diseño (stored in projects.phase).
  const [phase, setPhase] = useState(2);
  const [specStep, setSpecStep] = useState(1);
  const [genStep, setGenStep] = useState<number | null>(null);
  const [suggest, setSuggest] = useState(false);
  const [libTab, setLibTab] = useState<'tex' | 'mod' | null>(null);
  // Module designer open on a placed module (its id).
  const [designId, setDesignId] = useState<number | null>(null);
  const [cotas, setCotas] = useState(true);
  const [altos, setAltos] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [dark, setDark] = useState(false);
  const [open, setOpen] = useState(false);
  const [shadows, setShadows] = useState(shadowsOn);
  const toggleShadows = () => {
    setShadowsPref(!shadows);
    setShadows(!shadows);
    flash(shadows ? 'Sombras apagadas: la vista 3D va más ligera' : 'Sombras encendidas');
  };
  const toggleShadowsRef = useRef(toggleShadows);
  toggleShadowsRef.current = toggleShadows;
  // Phones and small tablets start with the 3D view clear; the panels slide over it.
  const narrow = typeof window !== 'undefined' && window.innerWidth <= 900;
  const [leftOpen, setLeftOpen] = useState(!narrow);
  const [rightOpen, setRightOpen] = useState(!narrow);
  const [leftTab, setLeftTab] = useState<LeftTab>('modulos');
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('Todos');
  const [applyTo, setApplyTo] = useState<'todo' | 'modulo'>('todo');
  const [replaceMode, setReplaceMode] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [tip, setTip] = useState<string | null>(null);
  const [save, setSave] = useState<SaveState>({ kind: 'saved', at: new Date().toISOString() });
  const [conflict, setConflict] = useState<Extract<SyncEvent, { type: 'conflict' }> | null>(null);
  const viewer = useRef<Viewer | null>(null);
  const dirty = useRef(false);
  const saving = useRef(false);
  /** Current id: a project created offline gets its server id when it syncs. */
  const pid = useRef(id);
  /** Route change caused by that id swap (not a real navigation). */
  const remapTarget = useRef<string | null>(null);
  const sync = useSyncStatus();
  const pending = !!sync?.pending.includes(pid.current);
  // Cover photo for Mis proyectos: regenerated after saves when the design changed.
  const coverUrl = useRef<string | null>(null);
  const coverSig = useRef('');
  const coverBusy = useRef(false);

  const applyProject = useCallback((p: ProjectDetail) => {
    pid.current = p.id;
    setProject(p);
    setData(p.data);
    setName(p.name);
    setCurrency(p.currency);
    setPhase(p.status === 'aprobado' || p.phase >= 3 ? 3 : p.phase <= 1 ? 1 : 2);
    if (p.coverUrl) coverSig.current = JSON.stringify([p.data.mods, p.data.mats, p.data.room, p.data.ops]);
    hist.current = [];
    fut.current = [];
    dirty.current = false;
    setSave({ kind: 'saved', at: p.updatedAt });
  }, []);
  const followRemap = useCallback(
    (to: string) => {
      // The crash-proof draft follows the project to its server id; left under the old id it would roll back later edits.
      clearDraft(pid.current);
      pid.current = to;
      remapTarget.current = to;
      if (dirty.current) {
        const snap = snapshotRef.current();
        if (snap) stashDraft(to, snap);
      }
      setProject((p) => (p ? { ...p, id: to } : p));
      nav(`/proyectos/${to}`, { replace: true });
    },
    [nav],
  );

  // ---------- load (this device first when it has unsent work; works offline) ----------
  useEffect(() => {
    if (remapTarget.current === id) {
      remapTarget.current = null;
      return;
    }
    let dead = false;
    Promise.all([getProject(id), getCatalog()])
      .then(([{ project: p, redirect }, c]) => {
        if (dead) return;
        installEngine(c.materials);
        setCatalog(c);
        applyProject(p);
        // Everything is shown in the organisation's base currency (RD$).
        setCurrency(c.pricing.baseCurrency);
        if (redirect) followRemap(redirect);
      })
      .catch((e) => !dead && setLoadError(e instanceof ApiError ? e.message : 'No se pudo abrir el proyecto.'));
    return () => {
      dead = true;
    };
  }, [id, applyProject, followRemap]);

  const readOnly = !project || project.status === 'aprobado' || !canEdit(me, project.access) || conflict != null;
  const materialsByCode = useMemo<Record<string, CatalogMaterial>>(() => Object.fromEntries((catalog?.materials ?? []).map((m) => [m.code, m])), [catalog]);
  const rate = project?.pricesFrozen ? project.estimate.rate : (catalog?.pricing.exchangeRateDopPerUsd ?? 60);
  const ctx = catalog?.context;
  const estimate = useMemo(() => (data && ctx ? computeEstimate(data, ctx, currency) : null), [data, ctx, currency]);
  const issues = useMemo(() => (data && ctx ? validateProject(data, ctx) : []), [data, ctx]);
  const selMod = data?.mods.find((m) => m.id === sel) ?? null;
  /** Every selected module that still exists, primary last. */
  const selIds = useMemo(() => {
    const ids = [...extra, ...(sel != null ? [sel] : [])];
    return ids.filter((id, i) => ids.indexOf(id) === i && !!data?.mods.some((m) => m.id === id));
  }, [extra, sel, data]);
  /** Plain click selects one module; Ctrl/⌘ + click adds it or takes it out of the selection. */
  const pickModule = useCallback(
    (id: number | null, add?: boolean) => {
      if (!add || id == null) return setSel(id);
      if (id === sel) {
        const rest = [...extra];
        setSelOne(rest.pop() ?? null);
        setExtra(rest);
      } else if (extra.includes(id)) setExtra(extra.filter((x) => x !== id));
      else {
        setExtra(sel != null ? [...extra, sel] : extra);
        setSelOne(id);
      }
    },
    [sel, extra, setSel],
  );

  // ---------- edits (undo/redo like the prototype's commit) ----------
  const commit = useCallback(
    (next: ProjectData) => {
      if (readOnly || !data) return;
      hist.current = [...hist.current.slice(-40), data];
      fut.current = [];
      dirty.current = true;
      setData(next);
      setSave({ kind: 'dirty' });
    },
    [data, readOnly],
  );
  // Modules placed from the library before their model's boards were read get them now, and become native
  // modules (doors and drawers from their boards). Saved like any edit, without an undo step.
  useEffect(() => {
    if (!data || !catalog || readOnly) return;
    const defs = catalog.context.modules;
    let changed = false;
    const mods = data.mods.map((m) => {
      const def = defs[m.code];
      let next = m;
      if (!m.panels?.length && def?.panels?.length && (m.glb || def.glb)) next = { ...next, panels: def.panels, pdim: def.pdim };
      // How the model is drawn (native or as-is) follows the library.
      if (m.glb && def && def.draw !== m.draw) {
        const { draw: _d, ...rest } = next;
        next = (def.draw ? { ...rest, draw: def.draw } : rest) as typeof m;
      }
      // Native doors and drawers read from the boards (the module opens and is drawn like a catalogue one).
      if (next.panels?.length && !next.fr.length) {
        const fr = def?.panels?.length && def.fr.length ? def.fr : frontsFromPanels(next);
        if (fr.length) next = { ...next, fr: structuredClone(fr) };
      }
      if (next !== m) changed = true;
      return next;
    });
    if (!changed) return;
    dirty.current = true;
    setData({ ...data, mods });
    setSave({ kind: 'dirty' });
  }, [data, catalog, readOnly]);
  const undo = useCallback(() => {
    if (!data || !hist.current.length) return;
    fut.current = [data, ...fut.current];
    const prev = hist.current[hist.current.length - 1]!;
    hist.current = hist.current.slice(0, -1);
    dirty.current = true;
    setData(prev);
    setSave({ kind: 'dirty' });
    force((n) => n + 1);
  }, [data]);
  const redo = useCallback(() => {
    if (!data || !fut.current.length) return;
    hist.current = [...hist.current, data];
    const next = fut.current[0]!;
    fut.current = fut.current.slice(1);
    dirty.current = true;
    setData(next);
    setSave({ kind: 'dirty' });
    force((n) => n + 1);
  }, [data]);

  // ---------- save: always to this device first; the sync queue uploads it ----------
  const localSnapshot = (): LocalSave | null => {
    if (!data) return null;
    const nm = name.trim() || data.pname;
    const est = ctx ? computeEstimate(data, ctx, currency) : null;
    return {
      name: nm,
      currency,
      phase,
      ...(coverUrl.current ? { coverUrl: coverUrl.current } : {}),
      data: { ...data, pname: nm },
      estimate: est ? { amount: est.total, currency, rate } : undefined,
      moduleCount: data.mods.length,
    };
  };
  const snapshotRef = useRef(localSnapshot);
  snapshotRef.current = localSnapshot;
  /** Saves on this device (instant, works offline). Resolves true when nothing is left unsaved locally. */
  const doSave = useCallback(async (): Promise<boolean> => {
    if (!project || !data || readOnly) return !dirty.current;
    while (saving.current) await new Promise((r) => setTimeout(r, 50));
    if (!dirty.current && save.kind !== 'dirty') return true;
    saving.current = true;
    dirty.current = false;
    setSave({ kind: 'saving' });
    try {
      const payload = snapshotRef.current();
      if (!payload) return false;
      const newId = await saveProject(pid.current, payload);
      coverUrl.current = null;
      clearDraft(pid.current);
      if (newId !== pid.current) followRemap(newId);
      setSave(dirty.current ? { kind: 'dirty' } : { kind: 'saved', at: new Date().toISOString() });
      return true;
    } catch (e) {
      dirty.current = true;
      setSave({ kind: 'error', message: e instanceof ApiError ? e.message : 'No se pudo guardar en este dispositivo.' });
      return false;
    } finally {
      saving.current = false;
    }
  }, [project, data, readOnly, followRemap, save.kind]);

  /** Sending to the client or approving needs the latest version on the server, not just on this device. */
  const ensureOnServer = useCallback(async () => {
    if (!(await doSave())) return false;
    if (await flushProject(pid.current)) return true;
    flash('Sin conexión: tus cambios están guardados en este dispositivo. Conéctate a internet para enviar o aprobar.');
    return false;
  }, [doSave, flash]);

  // Autosave shortly after the last change (it is a local write).
  useEffect(() => {
    if (save.kind !== 'dirty' || readOnly) return;
    const t = setTimeout(() => doSave(), 800);
    return () => clearTimeout(t);
  }, [save, doSave, readOnly]);

  // Sync results for this project.
  useEffect(
    () =>
      onSyncEvent((e) => {
        if (e.type === 'remap') {
          if (e.from === pid.current) followRemap(e.to);
        } else if (e.projectId !== pid.current) return;
        else if (e.type === 'synced') setProject((p) => (p ? { ...p, ...e.project, data: p.data, name: p.name, currency: p.currency } : p));
        else if (e.type === 'conflict') {
          // Edits made while the conflicting save was in flight belong to this user's copy; the draft must not
          // be replayed onto the original later (it would overwrite the other person's work).
          if (dirty.current) {
            const snap = snapshotRef.current();
            if (snap) void saveProject(e.copyId, snap).catch(() => {});
            dirty.current = false;
          }
          clearDraft(e.projectId);
          setConflict(e);
        }
        else if (e.type === 'dropped') flash(e.message);
      }),
    [followRemap, flash],
  );

  // Crash-proof draft: a synchronous copy of every unsaved change, replayed on the next start if the tab
  // is killed before the IndexedDB write finishes (phones do this to background apps).
  useEffect(() => {
    if (!dirty.current) return;
    const snap = snapshotRef.current();
    if (snap) stashDraft(pid.current, snap);
  }, [data, name, currency]);

  // After a save, refresh the cover if the design changed (small offscreen 3D photo, uploaded as a miniatura).
  useEffect(() => {
    if (save.kind !== 'saved' || readOnly || !project || !data?.mods.length || coverBusy.current) return;
    const sig = JSON.stringify([data.mods, data.mats, data.room, data.ops]);
    if (sig === coverSig.current) return;
    const t = setTimeout(async () => {
      coverBusy.current = true;
      coverSig.current = sig;
      try {
        const url = await snapshot(sceneCfg(data, { sel: null, cotas: false, altos: true, dark: false }), { w: 480, h: 300 });
        if (!url) return;
        const bin = atob(url.slice(url.indexOf(',') + 1));
        const blob = new Blob([Uint8Array.from(bin, (ch) => ch.charCodeAt(0))], { type: 'image/jpeg' });
        const up = await uploadFile('miniatura', blob, 'portada.jpg', project.id);
        coverUrl.current = up.url;
        dirty.current = true;
        setSave({ kind: 'dirty' });
      } catch {
        // A missing cover is cosmetic: Mis proyectos falls back to the drawing.
      } finally {
        coverBusy.current = false;
      }
    }, 1500);
    return () => clearTimeout(t);
  }, [save, readOnly, project, data]);

  // Saving is a local write, so flush it whenever the page may go away (tab hidden, app closed on a
  // phone, leaving the editor) instead of waiting for the autosave timer.
  const saveRef = useRef(doSave);
  saveRef.current = doSave;
  useEffect(() => {
    const flush = () => {
      if (!dirty.current) return;
      const s = snapshotRef.current();
      if (s) stashDraft(pid.current, s);
      void saveRef.current();
    };
    const onHide = () => document.visibilityState === 'hidden' && flush();
    const onUnload = (e: BeforeUnloadEvent) => {
      if (!dirty.current) return;
      flush();
      e.preventDefault();
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', flush);
    window.addEventListener('beforeunload', onUnload);
    return () => {
      flush();
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', flush);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, []);

  // ---------- keyboard ----------
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/INPUT|SELECT|TEXTAREA/.test((e.target as HTMLElement)?.tagName ?? '')) return;
      const k = e.key.toLowerCase();
      const mod = e.ctrlKey || e.metaKey;
      if (mod && k === 'z') {
        e.preventDefault();
        e.shiftKey ? redo() : undo();
      } else if ((e.ctrlKey || e.metaKey) && k === 'y') {
        e.preventDefault();
        redo();
      } else if ((e.ctrlKey || e.metaKey) && k === 's') {
        e.preventDefault();
        doSave();
      } else if (mod && k === 'p') {
        e.preventDefault();
        if (data) setPrinting(true);
      } else if (phase === 2 && data && mod && k === 'a') {
        e.preventDefault();
        const ids = data.mods.map((m) => m.id);
        if (!ids.length) return;
        setSelOne(ids[ids.length - 1]!);
        setExtra(ids.slice(0, -1));
        flash(`${ids.length} módulos seleccionados · Ctrl+C copiar · Ctrl+D eliminar`);
      } else if (phase === 2 && data && mod && (k === 'c' || k === 'x') && selIds.length && !window.getSelection()?.toString()) {
        e.preventDefault();
        copyModules(data.mods.filter((m) => selIds.includes(m.id)));
        const n = selIds.length;
        if (k === 'c') return flash(`${n === 1 ? 'Módulo copiado' : `${n} módulos copiados`} · Ctrl+V para pegar`);
        if (readOnly) return flash('Solo lectura: se copiaron, pero no se pueden quitar.');
        commit(removeModules(data, selIds));
        setSel(null);
        flash(`${n === 1 ? 'Módulo cortado' : `${n} módulos cortados`} · Ctrl+V para pegar`);
      } else if (phase === 2 && data && mod && k === 'v') {
        e.preventDefault();
        if (readOnly) return flash('Solo lectura: no se puede pegar.');
        const clip = clipboardModules();
        if (!clip.length) return flash('No hay módulos copiados. Selecciónalos y presiona Ctrl+C.');
        const r = pasteModules(data, clip);
        if (!r.ids.length) return flash('No hay espacio libre en los muros para pegar.');
        commit(r.project);
        setSelOne(r.ids[r.ids.length - 1]!);
        setExtra(r.ids.slice(0, -1));
        setRightOpen(true);
        flash(`${r.ids.length === 1 ? 'Módulo pegado' : `${r.ids.length} módulos pegados`}${r.failed ? ` · ${r.failed} no cupieron` : ''} · Ctrl+Z para deshacer`);
      } else if (phase === 2 && data && ((mod && k === 'd') || e.key === 'Delete' || e.key === 'Backspace')) {
        if (mod) e.preventDefault(); // Ctrl+D would bookmark the page
        if (!selIds.length) return mod ? flash('Selecciona módulos para eliminar (Ctrl+clic para varios).') : undefined;
        if (readOnly) return;
        e.preventDefault();
        commit(removeModules(data, selIds));
        flash(`${selIds.length === 1 ? `Módulo ${selIds[0]} eliminado` : `${selIds.length} módulos eliminados`} · Ctrl+Z para deshacer`);
        setSel(null);
      } else if (phase === 2 && data && !mod && !readOnly && k === 'm') {
        setView('3d');
        setMove3d((v) => !v);
      } else if (phase === 2 && data && !mod && !readOnly && k === 'g') {
        turnBy(e.shiftKey ? -15 : 15);
      } else if (phase === 2 && data && !mod && k === 's') {
        toggleShadowsRef.current();
      } else if (e.key === 'Escape') {
        setSel(null);
        setReplaceMode(false);
        setMove3d(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo, doSave, data, readOnly, commit, flash, phase, selIds, setSel]);

  // ---------- derived drawings ----------
  // While dragging in 3D the whole scene follows (worktop, neighbours), redrawn at most every 120 ms.
  const [drag3dData, setDrag3dData] = useState<ProjectData | null>(null);
  const cfg = useMemo(() => (data ? sceneCfg(drag3dData ?? data, { sel, sels: selIds, cotas, altos, dark }) : null), [data, drag3dData, sel, selIds, cotas, altos, dark]);
  const isoDrawing = useMemo(() => (data ? iso(data, materialsByCode as never, { sel, cotas, altos, zoom }) : null), [data, materialsByCode, sel, cotas, altos, zoom]);
  // ---------- moving modules by dragging them in the plan (mouse, finger or pen) ----------
  const [drag, setDrag] = useState<{ id: number; place: Place } | null>(null);
  const dragStart = useRef<{ id: number; place: Place; cx: number; cy: number; x: number; y: number; sx: number; sy: number; moved: boolean; pointer: number } | null>(null);
  const planData = useMemo(() => (data && drag ? moveModule(data, drag.id, drag.place) : data), [data, drag]);
  const planDrawing = useMemo(() => (planData && view === 'planta' ? plan(planData, { sel, sels: selIds, altos, cotas }) : null), [planData, view, sel, selIds, altos, cotas]);
  const toPlan = (e: React.PointerEvent, host: HTMLElement) => {
    const svg = host.querySelector('svg');
    const ctm = svg?.getScreenCTM();
    if (!svg || !ctm) return null;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const q = pt.matrixTransform(ctm.inverse());
    return { x: q.x, y: q.y };
  };
  const placeLabel = (pl: Place) => (pl.wall === 'F' ? `Isla · X ${Math.round(pl.x)} · Y ${Math.round(pl.y)} cm` : `Muro ${pl.wall} · ${Math.round(pl.pos)} cm desde la esquina`);
  const planPointer = {
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => {
      if (readOnly || !data || view !== 'planta' || (e.pointerType === 'mouse' && e.button !== 0)) return;
      const id = Number((e.target as Element).closest('[data-mid]')?.getAttribute('data-mid'));
      const m = id ? data.mods.find((x) => x.id === id) : undefined;
      const pt = m && toPlan(e, e.currentTarget);
      if (!m || !pt) return;
      const g = geo(m, data.room);
      dragStart.current = { id, place: placeOf(m), cx: (g.x0 + g.x1) / 2, cy: (g.y0 + g.y1) / 2, x: pt.x, y: pt.y, sx: e.clientX, sy: e.clientY, moved: false, pointer: e.pointerId };
    },
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => {
      const st = dragStart.current;
      if (!st || st.pointer !== e.pointerId) return;
      if (!st.moved) {
        if (Math.hypot(e.clientX - st.sx, e.clientY - st.sy) < 6) return;
        st.moved = true;
        // Captured only once it is a drag, so a plain tap still selects the module.
        e.currentTarget.setPointerCapture(e.pointerId);
      }
      const pt = toPlan(e, e.currentTarget);
      if (!pt) return;
      // The module follows the pointer (onto another wall when it gets closer to it).
      const place = data && placeAt(data, st.id, st.cx + pt.x - st.x, st.cy + pt.y - st.y);
      if (place) setDrag({ id: st.id, place });
    },
    onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => {
      const st = dragStart.current;
      dragStart.current = null;
      if (!st || !st.moved || !data || !drag) return setDrag(null);
      if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
      const next = moveModule(data, st.id, drag.place);
      setDrag(null);
      const m = next.mods.find((x) => x.id === st.id)!;
      commit(next);
      setSel(st.id);
      flash(`Módulo ${st.id} movido · ${placeLabel(placeOf(m))} · Ctrl+Z para deshacer`);
    },
    onPointerCancel: () => {
      dragStart.current = null;
      setDrag(null);
    },
  };
  // ---------- moving and turning modules in the 3D view (button «Mover muebles») ----------
  const [move3d, setMove3d] = useState(false);
  const [drag3dLabel, setDrag3dLabel] = useState<string | null>(null);
  const drag3d = useRef<{ id: number; cx: number; cy: number; next: ProjectData | null; t: number } | null>(null);
  useEffect(() => {
    viewer.current?.setMoveMode(move3d && !readOnly && view === '3d');
  }, [move3d, readOnly, view]);
  const onDrag3d = (id: number, dx: number, dz: number, ph: 'start' | 'move' | 'end' | 'cancel') => {
    if (!data || readOnly) return;
    if (ph === 'start') {
      const m = data.mods.find((x) => x.id === id);
      const g = m && geo(m, data.room);
      drag3d.current = g ? { id, cx: (g.x0 + g.x1) / 2, cy: (g.y0 + g.y1) / 2, next: null, t: 0 } : null;
      return;
    }
    const st = drag3d.current;
    if (!st || st.id !== id) return;
    const place = placeAt(data, id, st.cx + dx, st.cy + dz);
    const next = place ? moveModule(data, id, place) : data;
    const m = next.mods.find((x) => x.id === id)!;
    if (ph === 'move') {
      st.next = next;
      viewer.current?.previewMove(id, { wall: m.wall, pos: m.pos, x: m.x, y: m.y, rot: m.rot });
      setDrag3dLabel(placeLabel(placeOf(m)));
      const now = Date.now();
      if (now - st.t > 120) {
        st.t = now;
        setDrag3dData(next);
      }
      return;
    }
    drag3d.current = null;
    setDrag3dLabel(null);
    setDrag3dData(null);
    const moved = Math.abs(dx) + Math.abs(dz) >= 1 && st.next;
    if (ph === 'cancel' || !moved) {
      // Back where it was (a plain tap selects it).
      const o = data.mods.find((x) => x.id === id)!;
      viewer.current?.previewMove(id, { wall: o.wall, pos: o.pos, x: o.x, y: o.y, rot: o.rot });
      return;
    }
    commit(next);
    setSel(id);
    flash(`Módulo ${id} movido · ${placeLabel(placeOf(m))} · Ctrl+Z para deshacer`);
  };
  const turnBy = (deg: number) => {
    if (!data || readOnly) return;
    if (sel == null) return flash('Selecciona un mueble para girarlo (tócalo en la vista).');
    const m0 = data.mods.find((x) => x.id === sel);
    if (!m0) return;
    const next = rotateModule(data, sel, deg);
    const m = next.mods.find((x) => x.id === sel)!;
    setDrag3dData(null);
    commit(next);
    flash(m.wall === 'F' ? `Módulo ${sel} girado a ${Math.round(m.rot ?? 0)}° · Ctrl+Z para deshacer` : `Módulo ${sel} pasado al muro ${m.wall}: los altos van siempre en un muro · Ctrl+Z para deshacer`);
  };
  const setRot = (deg: number, preview?: boolean) => {
    if (!data || readOnly || sel == null) return;
    const next = setRotation(data, sel, deg);
    if (preview) return setDrag3dData(next);
    setDrag3dData(null);
    commit(next);
  };
  const turnSelected = () => turnBy(90);

  const [instPlan, setInstPlan] = useState(false);
  const elevDrawing = useMemo(() => (data && view === 'alzado' ? elev(data, wall, materialsByCode as never, { sel, sels: selIds, altos, cotas, inst: instPlan }) : null), [data, view, wall, materialsByCode, sel, selIds, altos, cotas, instPlan]);
  // «Ver desde atrás»: each press goes to the next wall with furniture or installations.
  const behindIdx = useRef(-1);
  const viewBehind = () => {
    if (!data) return;
    const ws = (['A', 'B', 'C', 'D'] as const).filter((w) => data.mods.some((m) => m.wall === w) || data.pts.some((q) => q.wall === w));
    if (!ws.length) return;
    behindIdx.current = (behindIdx.current + 1) % ws.length;
    const w = ws[behindIdx.current]!;
    setView('3d');
    viewer.current?.viewBehind(w);
    flash(`Viendo el muro ${w} desde atrás: el muro se oculta y se ven la parte trasera de los muebles y las instalaciones · toca otra vez para el siguiente muro`);
  };

  if (loadError)
    return (
      <div style={{ height: '100dvh', display: 'grid', placeItems: 'center', background: 'var(--color-bg)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 420 }}>
          <h2 style={{ margin: 0 }}>No se pudo abrir el proyecto</h2>
          <p style={{ margin: 0 }}>{loadError}</p>
          <button type="button" className="btn btn-primary" onClick={() => nav('/')}>
            Volver a mis proyectos
          </button>
        </div>
      </div>
    );
  if (!project || !data || !catalog || !estimate) return <FullScreenLoader label="Abriendo proyecto" />;

  // ---------- actions ----------
  const onAdd = (def: ModuleDefinition) => {
    const tpl = templateOf(def);
    if (replaceMode && sel) {
      commit(replaceModule(data, sel, tpl));
      setReplaceMode(false);
      flash(`Módulo ${sel} reemplazado por ${def.name}`);
      return;
    }
    if (tpl.cover) {
      // Over the selected upper or column (else the first upper), from its top to the ceiling.
      const c = addCover(data, tpl, sel);
      if (!c) return flash('Primero coloca las alacenas o columnas: el cubre falta va sobre ellas, hasta el techo.');
      commit(c.project);
      setSel(c.id);
      setRightOpen(true);
      const m = c.project.mods.find((x) => x.id === c.id)!;
      return flash(`${def.name} sobre el muro ${c.spot.wall}: ${m.h} cm hasta el techo`);
    }
    const r = addModule(data, tpl);
    if (!r) return flash(`No hay espacio libre en los muros para ${def.name}`);
    commit(r.project);
    setSel(r.id);
    setRightOpen(true);
    flash(r.spot.wall === 'F' ? `${def.name} colocado como isla (sin espacio en muros)` : `${def.name} agregado en el muro ${r.spot.wall}`);
  };
  const onMaterial = (group: 'cuerpo' | 'frentes' | 'encimera' | 'jaladeras', code: string) => commit(applyMaterial(data, group, code, applyTo === 'modulo' ? sel : null));
  const patchSel = (patch: Partial<ModuleInstance>) => sel && commit(updateModule(data, sel, patch));
  const markDirty = () => {
    if (readOnly) return;
    dirty.current = true;
    setSave({ kind: 'dirty' });
  };
  const goPhase = (n: number) => {
    if (n === phase) return;
    if (project.status === 'aprobado' && n < 3) return flash('El proyecto está aprobado. Para cambiar el diseño usa «Reabrir para cambios» en Aprobación.');
    setPhase(n);
    setSel(null);
    markDirty();
  };
  const onStatus = (status: ProjectDetail['status']) => {
    setProject((p) => (p ? { ...p, status } : p));
    // Refresh this device's copy too, so it carries the new status and version.
    getProject(pid.current)
      .then((r) => setProject((cur) => (cur ? { ...cur, ...r.project, data: cur.data, name: cur.name } : r.project)))
      .catch(() => {});
  };
  /** Own modules for the generated layout: the project's choices over the organisation's defaults. */
  const withOwnMods = (p: ProjectData): ProjectData => {
    const mods = { ...(catalog?.moduleDefaults ?? {}), ...((p.prefs as { mods?: Record<string, string> }).mods ?? {}) };
    return { ...p, prefs: { ...p.prefs, mods } };
  };
  const generate = () => {
    if (readOnly || genStep != null) return;
    const base = data;
    const tick = 450;
    setGenStep(0);
    for (let i = 0; i < GEN_STEPS.length; i++) setTimeout(() => setGenStep(i + 1), tick * (i + 1));
    setTimeout(() => {
      const g = generateDesign(withOwnMods(base), catalog.context.modules);
      commit({ ...base, mods: g.mods, mats: g.mats });
      setPhase(2);
      setGenStep(null);
      setSel(null);
      setView('3d');
      flash(g.notes.length ? `Distribución generada con ${g.mods.length} módulos · ${g.notes[0]}` : `Distribución generada con ${g.mods.length} módulos · Ctrl+Z para deshacer`);
    }, tick * GEN_STEPS.length + 350);
  };
  const sig = (mods: ModuleInstance[]) => mods.map((m) => `${m.code}${m.wall}${m.pos ?? m.x}${m.w}`).join();
  const alts = suggest
    ? (data.ptype === 'cocina'
        ? ([
            ['En L optimizada', 'L'],
            ['En L con isla', 'isla'],
            ['Lineal con columnas', 'lineal'],
          ] as const)
        : ([
            ['Puertas abatibles', 'lineal'],
            ['Vestidor abierto', 'abierto'],
            ['Mixto', 'U'],
          ] as const)
      ).map(([label, layout]) => {
        const g = generateDesign(withOwnMods({ ...data, layout }), catalog.context.modules);
        const next: ProjectData = { ...data, layout, mods: g.mods, mats: g.mats };
        const ml = g.mods.filter((m) => m.type !== 'upper' && m.type !== 'hood').reduce((a, m) => a + m.w, 0) / 100;
        return { label, next, current: sig(g.mods) === sig(data.mods), ml, total: computeEstimate(next, catalog.context, currency).total, art: iso(next, materialsByCode as never, { cotas: false, altos: true }) };
      })
    : [];
  const usedWalls: Wall[] = ['A', 'B', ...(['C', 'D'] as const).filter((w) => data.mods.some((m) => m.wall === w))];
  const is3d = view === '3d';
  const phases = [
    { n: 1, label: 'Especificaciones', short: 'Datos' },
    { n: 2, label: 'Diseño', short: 'Diseño' },
    { n: 3, label: 'Aprobación', short: 'Aprobar' },
  ];
  const controls: { k: string; icon: string; tip: string; key: string; on?: boolean; act: () => void }[] = [
    { k: 'zin', icon: 'zoom-in', tip: 'Acercar', key: '+', act: () => (is3d && viewer.current ? viewer.current.zoomBy(1.25) : setZoom((z) => Math.min(2.6, +(z * 1.25).toFixed(2)))) },
    { k: 'zout', icon: 'zoom-out', tip: 'Alejar', key: '−', act: () => (is3d && viewer.current ? viewer.current.zoomBy(1 / 1.25) : setZoom((z) => Math.max(0.6, +(z / 1.25).toFixed(2)))) },
    { k: 'fit', icon: 'scan', tip: 'Centrar vista', key: 'F', act: () => (is3d && viewer.current ? viewer.current.fit(false, 'auto') : setZoom(1)) },
    { k: 'rot', icon: 'rotate-cw', tip: 'Rotar cámara', key: 'R', act: () => (setView('3d'), viewer.current?.setAngle(25)) },
    { k: 'cotas', icon: 'ruler', tip: 'Cotas', key: 'C', on: cotas, act: () => setCotas(!cotas) },
    { k: 'altos', icon: 'layers', tip: 'Mostrar altos', key: 'A', on: altos, act: () => setAltos(!altos) },
    ...(readOnly
      ? []
      : [
          { k: 'move', icon: 'move', tip: move3d ? 'Terminar de mover' : 'Mover muebles (arrastra en 3D)', key: 'M', on: move3d, act: () => (setView('3d'), setMove3d(!move3d), !move3d && flash('Mover muebles: arrastra un mueble en la vista 3D. Se alinea solo con las esquinas y los vecinos.')) },
          { k: 'turn', icon: 'rotate-cw-square', tip: 'Girar mueble 90° (G: 15°)', key: 'G', act: turnSelected },
        ]),
    { k: 'behind', icon: 'scan-eye', tip: 'Ver desde atrás (instalaciones)', key: 'V', act: viewBehind },
    { k: 'open', icon: 'door-open', tip: open ? 'Cerrar puertas y cajones' : 'Abrir puertas y cajones', key: 'P', on: open, act: () => (setView('3d'), setOpen(!open), viewer.current?.setOpen(!open)) },
    { k: 'sombras', icon: shadows ? 'sun' : 'sun-dim', tip: shadows ? 'Apagar sombras' : 'Encender sombras', key: 'S', on: shadows, act: toggleShadows },
  ];
  const saveLabel =
    save.kind === 'saving'
      ? 'Guardando…'
      : save.kind === 'dirty'
        ? 'Cambios sin guardar'
        : save.kind === 'error'
          ? save.message
          : pending
            ? sync?.online
              ? 'Guardado · subiendo…'
              : 'Guardado en este dispositivo'
            : `Guardado ${relativeTime(save.at).toLowerCase()}`;
  const flatView = view === 'planta' ? planDrawing : view === 'alzado' ? elevDrawing : null;

  return (
    <div data-theme={dark ? 'dark' : 'light'} style={{ height: '100dvh', display: 'flex', flexDirection: 'column', background: 'var(--color-bg)', color: 'var(--color-text)', fontFamily: 'var(--font-body)', overflow: 'hidden', position: 'relative' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 14, height: 60, padding: '0 16px', borderBottom: '2px solid var(--color-divider)', background: 'var(--color-bg)', flex: 'none', minWidth: 0 }}>
        <Brand />
        <div style={{ width: 2, height: 28, background: 'var(--color-divider)', flex: 'none' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: '0 1 230px' }}>
          <input
            className="pname"
            value={name}
            disabled={readOnly}
            onChange={(e) => {
              setName(e.target.value);
              dirty.current = true;
              setSave({ kind: 'dirty' });
            }}
            aria-label="Nombre del proyecto"
            style={{ font: 'inherit', fontWeight: 600, fontSize: 14, color: 'var(--color-text)', background: 'transparent', border: '1px solid transparent', padding: '6px 8px', minWidth: 0, width: '100%' }}
          />
          <Icon name="pencil" size={13} style={{ opacity: 0.5 }} />
        </div>
        <nav className="phases" style={{ display: 'flex', alignItems: 'center', gap: 4, margin: '0 auto', flex: 'none' }}>
          {phases.map((p, i) => {
            const cur = p.n === phase;
            const done = p.n < phase || (p.n === 3 && project.status === 'aprobado');
            return (
              <div key={p.n} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button type="button" onClick={() => goPhase(p.n)} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 0, padding: 8, cursor: 'pointer', color: cur ? 'var(--color-text)' : MUTED, font: 'inherit', fontSize: 13, fontWeight: cur ? 800 : 600 }}>
                  <span style={{ width: 24, height: 24, display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 800, background: cur ? 'var(--color-accent)' : done ? 'var(--color-text)' : 'transparent', color: cur || done ? '#fff' : 'var(--color-text)', border: `2px solid ${cur ? 'var(--color-accent)' : done ? 'var(--color-text)' : 'var(--color-divider)'}` }}>
                    {done && !cur ? <Icon name="check" size={13} /> : p.n}
                  </span>
                  <span className="phase-label" style={{ whiteSpace: 'nowrap' }}>
                    {p.label}
                  </span>
                </button>
                {i < phases.length - 1 && <span style={{ width: 28, height: 2, background: 'var(--color-divider)' }} />}
              </div>
            );
          })}
        </nav>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none' }}>
          <span className="save-state" style={{ fontSize: 12, color: save.kind === 'error' ? 'var(--color-accent-700)' : MUTED, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6, marginRight: 6 }}>
            <Icon name={save.kind === 'error' ? 'cloud-alert' : save.kind === 'saved' && !pending ? 'cloud-check' : save.kind === 'saved' && !sync?.online ? 'hard-drive' : 'cloud-upload'} size={14} />
            {readOnly && !conflict ? 'Solo lectura' : saveLabel}
          </span>
          <SyncBadge compact />
          <button type="button" className="btn btn-icon ed-hide-xs" title="Deshacer (Ctrl+Z)" aria-label="Deshacer" onClick={undo} disabled={readOnly || !hist.current.length}>
            <Icon name="undo-2" size={18} />
          </button>
          <button type="button" className="btn btn-icon ed-hide-xs" title="Rehacer (Ctrl+Y)" aria-label="Rehacer" onClick={redo} disabled={readOnly || !fut.current.length}>
            <Icon name="redo-2" size={18} />
          </button>
          <button type="button" className="btn btn-icon ed-hide-xs" title="Versiones del proyecto" aria-label="Versiones" onClick={() => setVersionsOpen(true)}>
            <Icon name="history" size={17} />
          </button>
          <button type="button" className="btn btn-icon ed-hide-xs" title="Bibliotecas de tableros y módulos" aria-label="Bibliotecas" onClick={() => setLibTab('tex')}>
            <Icon name="library" size={17} />
          </button>
          <button type="button" className="btn btn-icon ed-hide-xs" title="Modo oscuro del editor" aria-label="Modo oscuro" onClick={() => setDark(!dark)}>
            <Icon name={dark ? 'sun' : 'moon'} size={17} />
          </button>
          <div style={{ width: 2, height: 28, background: 'var(--color-divider)', margin: '0 4px' }} />
          <button type="button" className="btn btn-primary" onClick={() => doSave()} disabled={readOnly || save.kind === 'saving'} title="Guardar (Ctrl+S)" style={{ height: 38 }}>
            <Icon name="save" />
            <span className="phase-label">Guardar</span>
          </button>
          <button type="button" className="btn btn-icon" title={project.access === 'propietario' ? 'Compartir' : 'Personas con acceso'} aria-label="Compartir" onClick={() => setShareOpen(true)}>
            <Icon name={project.shareCount > 0 || project.access !== 'propietario' ? 'users' : 'share-2'} size={18} />
          </button>
          <UserMenu />
        </div>
      </header>

      {/* Phones and tablets: the phase stepper and edit tools that do not fit in the header. */}
      <div className="ed-mbar" role="navigation" aria-label="Fases del proyecto">
        {phases.map((p) => {
          const cur = p.n === phase;
          const done = p.n < phase || (p.n === 3 && project.status === 'aprobado');
          return (
            <button key={p.n} type="button" onClick={() => goPhase(p.n)} aria-current={cur ? 'step' : undefined} className={cur ? 'ed-mstep on' : 'ed-mstep'}>
              <span className="ed-mnum">{done && !cur ? <Icon name="check" size={12} /> : p.n}</span>
              <span className="ed-mlabel">{p.short}</span>
            </button>
          );
        })}
        <span style={{ flex: 1 }} />
        <button type="button" className="btn btn-icon ed-mtool" title="Deshacer" aria-label="Deshacer" onClick={undo} disabled={readOnly || !hist.current.length}>
          <Icon name="undo-2" size={18} />
        </button>
        <button type="button" className="btn btn-icon ed-mtool" title="Rehacer" aria-label="Rehacer" onClick={redo} disabled={readOnly || !fut.current.length}>
          <Icon name="redo-2" size={18} />
        </button>
        <button type="button" className="btn btn-icon ed-mtool" title="Versiones" aria-label="Versiones" onClick={() => setVersionsOpen(true)}>
          <Icon name="history" size={17} />
        </button>
        <button type="button" className="btn btn-icon ed-mtool" title="Bibliotecas" aria-label="Bibliotecas" onClick={() => setLibTab('tex')}>
          <Icon name="library" size={17} />
        </button>
      </div>

      {readOnly && !conflict && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', background: project.status === 'aprobado' ? 'var(--color-accent-100)' : 'var(--color-neutral-200)', color: project.status === 'aprobado' ? 'var(--color-accent-800)' : 'var(--color-text)', fontSize: 13, borderBottom: '1px solid var(--color-divider)' }}>
          <Icon name={project.status === 'aprobado' ? 'lock' : 'eye'} size={15} />
          {project.status === 'aprobado' ? 'Proyecto aprobado: el diseño está bloqueado para producción. Para hacer cambios, usa «Reabrir para cambios» en Aprobación (o duplícalo).' : project.access === 'ver'
              ? `Solo lectura: ${project.ownerName ?? 'otra persona'} te compartió este proyecto para verlo. Puedes duplicarlo desde Mis proyectos para trabajar sobre una copia.`
              : 'Solo lectura: tu rol no permite editar proyectos.'}
        </div>
      )}

      {phase === 3 ? (
        <ApprovalView
          project={project}
          data={data}
          catalog={catalog}
          materialsByCode={materialsByCode}
          estimate={estimate}
          currency={currency}
          issues={issues}
          canManage={!readOnly}
          canReopen={project.status === 'aprobado' && canEdit(me, project.access) && conflict == null}
          orgName={me?.organization.name ?? 'Planner'}
          orgLogo={me?.organization.logoUrl}
          orgColor={me?.organization.brandColor}
          commit={commit}
          ensureSaved={ensureOnServer}
          onStatus={onStatus}
          flash={flash}
        />
      ) : phase === 1 ? (
        <SpecWizard
          data={data}
          step={specStep}
          setStep={setSpecStep}
          commit={commit}
          onGenerate={generate}
          currency={currency}
          readOnly={readOnly}
          flash={flash}
          materials={catalog.materials}
          modules={catalog.context.modules}
          moduleDefaults={catalog.moduleDefaults ?? {}}
          canSaveModuleDefaults={me?.user.role === 'admin'}
          onSavedModuleDefaults={(moduleDefaults) => setCatalog((c) => (c ? { ...c, moduleDefaults } : c))}
        />
      ) : (
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div className="ed-body" style={{ flex: 1, minHeight: 0, display: 'flex', position: 'relative' }}>
          {(leftOpen || rightOpen) && <div className="ed-scrim" onClick={() => (setLeftOpen(false), setRightOpen(false))} />}
          {leftOpen ? (
            <LeftPanel
              tab={leftTab}
              setTab={setLeftTab}
              onClose={() => setLeftOpen(false)}
              data={data}
              catalog={catalog}
              materialsByCode={materialsByCode}
              q={q}
              setQ={setQ}
              cat={cat}
              setCat={setCat}
              onAdd={onAdd}
              replaceMode={replaceMode}
              onCancelReplace={() => setReplaceMode(false)}
              sel={selMod}
              applyTo={applyTo}
              setApplyTo={setApplyTo}
              onMaterial={onMaterial}
              readOnly={readOnly}
              onPick={(i) => (setSel(i), setRightOpen(true))}
              onLibrary={canCreate(me) ? setLibTab : undefined}
              onPatch={(patch) => commit({ ...data, ...patch })}
            />
          ) : (
            <div className="ed-rail ed-rail-left" style={{ width: 48, flex: 'none', borderRight: '2px solid var(--color-divider)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, paddingTop: 8 }}>
              <button type="button" className="btn btn-icon" onClick={() => setLeftOpen(true)} title="Mostrar panel" aria-label="Mostrar panel">
                <Icon name="panel-left-open" size={18} />
              </button>
              {(
                [
                  ['modulos', 'layout-grid', 'Módulos'],
                  ['materiales', 'palette', 'Materiales'],
                  ['electro', 'refrigerator', 'Electro'],
                ] as const
              ).map(([k, ic, l]) => (
                <button type="button" key={k} className="btn btn-icon" onClick={() => (setLeftTab(k), setLeftOpen(true))} title={l} aria-label={l}>
                  <Icon name={ic} size={17} />
                </button>
              ))}
            </div>
          )}

          <div style={{ flex: 1, minWidth: 0, position: 'relative', background: 'var(--sp-canvas)', overflow: 'hidden' }}>
            {cfg && <div style={{ position: 'absolute', inset: 0, visibility: is3d ? 'visible' : 'hidden' }}><Viewer3D cfg={cfg} onSelect={(i, add) => { pickModule(i, add); if (i) setRightOpen(true); setReplaceMode(false); }} onDrag={onDrag3d} onViewer={(v) => { viewer.current = v; v?.setOpen(open); v?.setMoveMode(move3d && !readOnly && view === '3d'); }} fallback={<Svg drawing={isoDrawing} onPick={pickModule} />} /></div>}
            {flatView && (
              <div
                {...(view === 'planta' ? planPointer : {})}
                style={{ position: 'absolute', inset: '64px 72px 24px 32px', transform: `scale(${zoom})`, transformOrigin: 'center', touchAction: view === 'planta' && !readOnly ? 'none' : undefined }}
              >
                <Svg drawing={flatView} onPick={(i, add) => (pickModule(i, add), i && setRightOpen(true))} />
              </div>
            )}
            {view === '3d' && move3d && !readOnly && sel != null && data.mods.some((x) => x.id === sel) && !drag3dLabel && (
              <div role="group" aria-label="Girar el mueble seleccionado" style={{ position: 'absolute', left: '50%', bottom: 76, transform: 'translateX(-50%)', width: 'min(360px, calc(100% - 140px))', background: 'var(--color-bg)', border: '1px solid var(--color-divider)', boxShadow: 'var(--shadow-sm)', padding: '8px 10px', zIndex: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 4 }}>Girar · módulo {sel}</div>
                <RotateControl compact sel={data.mods.find((x) => x.id === sel)!} onTurn={turnBy} onRot={setRot} />
              </div>
            )}
            {view === '3d' && drag3dLabel && (
              <div role="status" style={{ position: 'absolute', left: '50%', bottom: 16, transform: 'translateX(-50%)', background: 'var(--color-text)', color: 'var(--color-bg)', padding: '8px 14px', fontSize: 13, fontWeight: 700, zIndex: 4, pointerEvents: 'none', whiteSpace: 'nowrap' }}>
                {drag3dLabel}
              </div>
            )}
            {view === 'planta' && drag && planData && (
              <div role="status" style={{ position: 'absolute', left: '50%', bottom: 16, transform: 'translateX(-50%)', background: 'var(--color-text)', color: 'var(--color-bg)', padding: '8px 14px', fontSize: 13, fontWeight: 700, zIndex: 4, pointerEvents: 'none', whiteSpace: 'nowrap' }}>
                {placeLabel(placeOf(planData.mods.find((x) => x.id === drag.id)!))}
              </div>
            )}

            <div style={{ position: 'absolute', top: 12, left: 12, right: 64, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', zIndex: 3 }}>
              <div style={{ display: 'flex', background: 'var(--color-bg)', border: '1px solid var(--color-divider)', boxShadow: 'var(--shadow-sm)' }}>
                {(
                  [
                    ['3d', '3D', 'box'],
                    ['planta', 'Planta', 'square-dashed'],
                    ['alzado', 'Alzado', 'panels-top-left'],
                  ] as const
                ).map(([k, l, ic]) => (
                  <button type="button" key={k} onClick={() => setView(k)} title={k === '3d' ? '3D Perspectiva' : l} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', font: 'inherit', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', border: 0, cursor: 'pointer', background: view === k ? 'var(--color-text)' : 'transparent', color: view === k ? 'var(--color-bg)' : 'var(--color-text)' }}>
                    <Icon name={ic} size={15} />
                    {l}
                  </button>
                ))}
              </div>
              {view === 'alzado' && (
                <div style={{ display: 'flex', background: 'var(--color-bg)', border: '1px solid var(--color-divider)', boxShadow: 'var(--shadow-sm)' }}>
                  {usedWalls.map((w) => (
                    <button type="button" key={w} onClick={() => setWall(w)} style={{ padding: '8px 12px', font: 'inherit', fontSize: 13, fontWeight: 800, border: 0, cursor: 'pointer', background: wall === w ? 'var(--color-text)' : 'transparent', color: wall === w ? 'var(--color-bg)' : 'var(--color-text)' }}>
                      Muro {w}
                    </button>
                  ))}
                  <button type="button" aria-pressed={instPlan} onClick={() => setInstPlan(!instPlan)} title="Plano de instalaciones: cada toma con su distancia desde la izquierda y su altura" style={{ padding: '8px 12px', font: 'inherit', fontSize: 13, fontWeight: 800, border: 0, borderLeft: '1px solid var(--color-divider)', cursor: 'pointer', background: instPlan ? 'var(--color-accent)' : 'transparent', color: instPlan ? '#fff' : 'var(--color-text)' }}>
                    Instalaciones
                  </button>
                </div>
              )}
              {!readOnly && (
                <button type="button" className="btn btn-primary" onClick={() => setSuggest(true)} style={{ height: 38, boxShadow: 'var(--shadow-md)' }}>
                  <Icon name="layout-dashboard" size={16} />
                  <span className="phase-label">Sugerir distribución automática</span>
                </button>
              )}
            </div>

            <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', flexDirection: 'column', background: 'var(--color-bg)', border: '1px solid var(--color-divider)', boxShadow: 'var(--shadow-sm)', zIndex: 3 }}>
              {controls.map((c, i) => (
                <div key={c.k} style={{ position: 'relative' }}>
                  <button type="button"
                    onClick={c.act}
                    onMouseEnter={() => setTip(c.k)}
                    onMouseLeave={() => setTip(null)}
                    onFocus={() => setTip(c.k)}
                    onBlur={() => setTip(null)}
                    aria-label={c.tip}
                    style={{ width: 40, height: 40, display: 'grid', placeItems: 'center', border: 0, cursor: 'pointer', background: c.on ? 'var(--color-text)' : 'transparent', color: c.on ? 'var(--color-bg)' : 'var(--color-text)', borderTop: i && i === 4 ? '2px solid var(--color-divider)' : undefined }}
                  >
                    <Icon name={c.icon} size={17} />
                  </button>
                  {tip === c.k && (
                    <span role="tooltip" style={{ position: 'absolute', right: 48, top: 8, whiteSpace: 'nowrap', background: 'var(--color-text)', color: 'var(--color-bg)', fontSize: 12, padding: '5px 9px', pointerEvents: 'none', zIndex: 5 }}>
                      {c.tip}
                      <span style={{ opacity: 0.6, marginLeft: 8 }}>{c.key}</span>
                    </span>
                  )}
                </div>
              ))}
            </div>

            {view !== 'planta' && data.mods.length > 0 && (
              <div className="minimap" style={{ position: 'absolute', left: 12, bottom: 12, width: 190, height: 160, background: 'var(--color-bg)', border: '1px solid var(--color-divider)', boxShadow: 'var(--shadow-sm)', padding: 6, display: 'flex', flexDirection: 'column', zIndex: 3 }}>
                <span style={{ fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', color: MUTED }}>Planta</span>
                <div style={{ flex: 1, minHeight: 0 }}>
                  <Svg drawing={plan(data, { sel, sels: selIds, cotas: false, nums: false })} onPick={pickModule} />
                </div>
              </div>
            )}
            {selIds.length > 1 && (
              <div role="status" style={{ position: 'absolute', left: '50%', bottom: 14, transform: 'translateX(-50%)', background: 'var(--color-text)', color: 'var(--color-bg)', boxShadow: 'var(--shadow-sm)', padding: '7px 12px', fontSize: 13, display: 'flex', gap: 12, alignItems: 'center', whiteSpace: 'nowrap', zIndex: 3 }}>
                <strong>{selIds.length} módulos seleccionados</strong>
                <span className="multi-keys" style={{ opacity: 0.75 }}>Ctrl+C copiar · Ctrl+X cortar · Ctrl+V pegar · Ctrl+D eliminar · Esc soltar</span>
              </div>
            )}
            {selMod && selIds.length <= 1 && (
              <div style={{ position: 'absolute', left: '50%', bottom: 14, transform: 'translateX(-50%)', background: 'var(--color-bg)', border: '1px solid var(--color-divider)', boxShadow: 'var(--shadow-sm)', padding: '7px 12px', fontSize: 13, display: 'flex', gap: 10, alignItems: 'center', whiteSpace: 'nowrap', zIndex: 3 }}>
                <span style={{ width: 20, height: 20, background: 'var(--color-accent)', color: '#fff', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 800 }}>{selMod.id}</span>
                <strong>{selMod.name}</strong>
                <span style={{ opacity: 0.7 }}>
                  {selMod.w * 10} × {selMod.h * 10} × {selMod.d * 10} mm
                </span>
              </div>
            )}
            {data.mods.length === 0 && (
              <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none', zIndex: 4 }}>
                <div style={{ width: 'min(440px,80%)', border: '2px dashed var(--color-divider)', padding: 32, background: 'color-mix(in srgb,var(--color-bg) 85%,transparent)', pointerEvents: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <Icon name="mouse-pointer-2" size={28} style={{ color: 'var(--color-accent)' }} />
                  <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.15 }}>Agrega tu primer módulo</div>
                  <div style={{ fontSize: 14, color: 'color-mix(in srgb,var(--color-text) 68%,transparent)' }}>Elige un módulo en el panel izquierdo; se coloca en el primer espacio libre del muro.</div>
                  <button type="button" className="btn btn-secondary" onClick={() => (setLeftOpen(true), setLeftTab('modulos'))} style={{ width: 'max-content' }}>
                    Ver módulos
                  </button>
                </div>
              </div>
            )}
          </div>

          {rightOpen ? (
            <RightPanel
              data={data}
              sel={selMod}
              materials={catalog.materials}
              materialsByCode={materialsByCode}
              estimate={estimate}
              currency={currency}
              rate={rate}
              readOnly={readOnly}
              onClose={() => setRightOpen(false)}
              onDeselect={() => setSel(null)}
              onDim={(k, v) => sel && commit(setDim(data, sel, k, v))}
              onFronts={(n) => sel && commit(setFrontCount(data, sel, n))}
              onPatch={patchSel}
              onHerraje={(v) => sel && commit({ ...data, herr: { ...(data.herr ?? {}), [String(sel)]: v } })}
              onPrice={(v) => patchSel({ pOv: v == null ? undefined : Math.max(0, v) })}
              onDuplicate={() => {
                if (!sel) return;
                const r = duplicateModule(data, sel);
                if (!r) return flash('No hay espacio libre para duplicarlo');
                commit(r.project);
                setSel(r.id);
                flash(`Módulo ${sel} duplicado como ${r.id}`);
              }}
              onReplace={() => {
                setReplaceMode(true);
                setLeftOpen(true);
                setLeftTab('modulos');
              }}
              onTurn={turnBy}
              onRot={setRot}
              onMove={(to) => {
                if (!sel) return;
                const next = moveModule(data, sel, to);
                commit(next);
              }}
              onLift={(z) => sel && commit(setElevation(data, sel, z))}
              onFitCover={() => {
                if (!sel) return;
                const next = fitCover(data, sel);
                if (next === data) return flash('No queda espacio entre los módulos y el techo para el cubre falta.');
                commit(next);
                flash('Cubre falta ajustado hasta el techo');
              }}
              onDesign={() => sel && setDesignId(sel)}
              onRemove={() => {
                if (!sel) return;
                commit(removeModule(data, sel));
                flash(`Módulo ${sel} eliminado · Ctrl+Z para deshacer`);
                setSel(null);
              }}
            />
          ) : (
            <div className="ed-rail ed-rail-right" style={{ width: 48, flex: 'none', borderLeft: '2px solid var(--color-divider)', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 8 }}>
              <button type="button" className="btn btn-icon" onClick={() => setRightOpen(true)} title="Mostrar propiedades" aria-label="Mostrar propiedades">
                <Icon name="panel-right-open" size={18} />
              </button>
            </div>
          )}
        </div>

        <BottomBar
          issues={issues}
          estimate={estimate}
          data={data}
          currency={currency}
          rate={rate}
          readOnly={readOnly}
          orgTaxRate={catalog.pricing.taxRate}
          onPick={(i) => (setSel(i), setRightOpen(true))}
          onPriceAdj={(patch) => commit({ ...data, priceAdj: { ...data.priceAdj, ...patch } })}
          onResetPrices={() => commit({ ...data, priceAdj: { ...data.priceAdj, inst: 8, desc: 0, final: null, counter: null }, mods: data.mods.map(({ pOv: _p, ...m }) => m as ModuleInstance) })}
          onApproval={() => goPhase(3)}
        />
      </div>
      )}

      {conflict != null && (
        <Dialog
          title="Tus cambios se guardaron como copia"
          onClose={() => {}}
          actions={
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={async () => {
                  const r = await getProject(pid.current).catch(() => null);
                  setConflict(null);
                  if (r) applyProject(r.project);
                }}
              >
                Ver la versión actual
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  dirty.current = false;
                  setConflict(null);
                  nav(`/proyectos/${conflict.copyId}`);
                }}
              >
                Abrir mi copia
              </button>
            </>
          }
        >
          {conflict.reason} Para que no se pierda nada, tu versión quedó guardada en el proyecto «{conflict.copyName}». Este proyecto conserva la versión del servidor.
        </Dialog>
      )}
      {versionsOpen && (
        <VersionsDialog
          projectId={pid.current}
          canEdit={!readOnly}
          ensureOnServer={ensureOnServer}
          onClose={() => setVersionsOpen(false)}
          onRestored={() => {
            // Refresh this device's copy from the server (nothing is pending after ensureOnServer).
            getProject(pid.current)
              .then((r) => {
                applyProject(r.project);
                if (catalog) setCurrency(catalog.pricing.baseCurrency);
                flash(`Versión restaurada · ${r.project.name}`);
              })
              .catch(() => flash('Versión restaurada; recarga el proyecto para verla.'));
          }}
        />
      )}
      {shareOpen && me && (
        <ShareDialog
          projectId={pid.current}
          projectName={name || project.name}
          myAccess={project.access}
          meId={me.user.id}
          onClose={() => setShareOpen(false)}
          onChange={(n) => setProject((p) => (p ? { ...p, shareCount: n } : p))}
        />
      )}
      {genStep != null && (
        <div style={{ position: 'absolute', inset: 0, background: 'color-mix(in srgb,var(--color-bg) 92%,transparent)', display: 'grid', placeItems: 'center', zIndex: 40 }}>
          <div style={{ width: 'min(440px,90%)', display: 'flex', flexDirection: 'column', gap: 16 }} role="status" aria-live="polite">
            <span style={{ width: 40, height: 40, border: '3px solid var(--color-neutral-300)', borderTopColor: 'var(--color-accent)', borderRadius: '50%', animation: 'spspin .9s linear infinite' }} />
            <div style={{ fontSize: 26, fontWeight: 800 }}>Generando distribución…</div>
            <div style={{ height: 4, background: 'var(--color-neutral-300)' }}>
              <div style={{ height: 4, background: 'var(--color-accent)', width: `${(genStep / GEN_STEPS.length) * 100}%`, transition: 'width .5s' }} />
            </div>
            {GEN_STEPS.map((label, i) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: i <= genStep ? 'var(--color-text)' : MUTED }}>
                <Icon name={i < genStep ? 'circle-check' : i === genStep ? 'loader' : 'circle'} size={16} />
                {label}
              </div>
            ))}
          </div>
        </div>
      )}
      {suggest && (
        <Dialog title="Distribuciones sugeridas" onClose={() => setSuggest(false)} width={960}>
          <p style={{ margin: '0 0 16px' }}>Calculadas con tus medidas, instalaciones y electrodomésticos. Elige una para reemplazar la escena actual (puedes deshacer).</p>
          <div className="alts" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 14 }}>
            {alts.map((a) => (
              <div key={a.label} style={{ display: 'flex', flexDirection: 'column', background: 'var(--color-bg)', border: `2px solid ${a.current ? 'var(--color-accent)' : 'var(--color-divider)'}` }}>
                <div className="alt-art" style={{ height: 190, background: 'var(--sp-canvas)', padding: 8 }}>
                  <Svg drawing={a.art} title={a.label} />
                </div>
                <div style={{ padding: '12px 14px 14px', display: 'flex', flexDirection: 'column', gap: 8, color: 'var(--color-text)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 800, fontSize: 16 }}>{a.label}</span>
                    {a.current && <span className="tag tag-accent">Actual</span>}
                  </div>
                  {(
                    [
                      ['Módulos', String(a.next.mods.length)],
                      ['Metros lineales', `${a.ml.toFixed(1).replace('.', ',')} m`],
                      ['Precio estimado', fmtMoney(a.total, currency)],
                    ] as const
                  ).map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                      <span style={{ color: MUTED }}>{k}</span>
                      <strong>{v}</strong>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      commit(a.next);
                      setSuggest(false);
                      setSel(null);
                      flash(`Distribución "${a.label}" aplicada · Ctrl+Z para deshacer`);
                    }}
                    style={{ justifyContent: 'space-between', marginTop: 4 }}
                  >
                    Usar esta distribución
                    <Icon name="arrow-right" size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Dialog>
      )}
      {designId != null && data.mods.some((m) => m.id === designId) && (
        <ModuleDesigner
          mode="project"
          initial={draftOfInstance(data.mods.find((m) => m.id === designId)!)}
          title={`Módulo ${designId} · ${data.mods.find((m) => m.id === designId)!.name}`}
          cats={[]}
          onCancel={() => setDesignId(null)}
          onSave={(d) => {
            commit(updateModule(data, designId, draftToInstancePatch(d)));
            setDesignId(null);
            flash(`Módulo ${designId} actualizado`);
          }}
        />
      )}
      {libTab && (
        <LibraryDialog
          initialTab={libTab}
          canWrite={canCreate(me)}
          onClose={() => setLibTab(null)}
          onChanged={() =>
            getCatalog()
              .then((c) => {
                installEngine(c.materials);
                setCatalog(c);
                flash('Biblioteca actualizada');
              })
              .catch(() => flash('Recarga la página para ver los cambios de la biblioteca.'))
          }
        />
      )}
      {printing && data && catalog && estimate && (
        <PrintSheet data={data} name={name || data.pname} orgName={me?.organization.name ?? 'Planner'} materials={catalog.context.materials} estimate={estimate} currency={currency} onDone={() => setPrinting(false)} />
      )}
      {toast}
      <style>{`
        .pname:hover{border-color:var(--color-divider)!important}
        .pname:focus{border-color:var(--color-accent)!important;outline:none;background:var(--color-surface)!important}
        .lib-card:hover{border-color:var(--color-accent)!important}
        .tab-btn:hover,.val-btn:hover{background:color-mix(in srgb,var(--color-text) 5%,transparent)!important}
        @media (max-width: 1440px){.phase-label{display:none!important}}
        @media (max-width: 1100px){.save-state{display:none!important}}
        @media (max-width: 700px){.brand-name{display:none!important}}
        @media (max-width: 900px){
          .phases,.minimap{display:none!important}
          .alts{grid-template-columns:minmax(0,1fr)!important}
          .ed-body>aside{position:absolute;top:0;bottom:0;z-index:30;width:min(88vw,340px)!important;box-shadow:var(--shadow-lg)}
          .ed-body .ed-left{left:0}
          .ed-body .ed-right{right:0}
          .ed-scrim{position:absolute;inset:0;z-index:29;background:rgba(0,0,0,.25)}
        }
        @media (min-width: 901px){.ed-scrim{display:none}.ed-mbar{display:none!important}}
        .ed-mbar{display:flex;align-items:center;gap:2px;padding:4px 8px;border-bottom:1px solid var(--color-divider);background:var(--color-bg);flex:none;min-height:44px}
        .ed-mstep{display:flex;align-items:center;gap:6px;background:none;border:0;padding:6px 8px;font:inherit;font-size:13px;font-weight:600;color:${MUTED};cursor:pointer;white-space:nowrap;min-height:40px}
        .ed-mstep.on{color:var(--color-text);font-weight:800}
        .ed-mnum{width:22px;height:22px;display:grid;place-items:center;font-size:12px;font-weight:800;border:2px solid var(--color-divider)}
        .ed-mstep.on .ed-mnum{background:var(--color-accent);border-color:var(--color-accent);color:#fff}
        @media (min-width: 561px){.ed-mtool{display:none!important}}
        @media (max-width: 360px){.ed-mstep:not(.on) .ed-mlabel{display:none}}
        @media (max-width: 700px){.alt-art{height:130px!important}}
        @media (max-width: 700px){.bb-issues{grid-template-columns:minmax(0,1fr)!important;max-height:45dvh!important}}
        @media (max-width: 560px){
          .ed-hide-xs,.bb-hide-xs,.bb-first{display:none!important}
          .bb-row{gap:8px!important;padding:6px 10px!important;justify-content:space-between}
          header{gap:6px!important;padding:0 8px!important}
          /* the collapsed side rails float over the 3D view instead of taking width */
          .ed-rail{position:absolute;z-index:20;width:auto!important;border:0!important;background:var(--color-bg);box-shadow:var(--shadow-sm);padding:4px!important}
          .ed-rail-left{left:8px;bottom:8px}
          .ed-rail-right{right:8px;bottom:8px}
        }
        @keyframes spspin{to{transform:rotate(360deg)}}
      `}</style>
      <span hidden>{fmtMoney(0, currency)}</span>
    </div>
  );
}
